import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Chrome, Shield, Smartphone, Bot, HardDrive, Monitor, Mail, ExternalLink, Send, CheckCircle, AlertCircle } from 'lucide-react';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setMessage('Veuillez saisir une adresse email valide');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Inscription réussie ! Merci de votre intérêt.');
        setMessageType('success');
        setEmail('');
      } else {
        setMessage(data.detail || 'Une erreur est survenue. Veuillez réessayer.');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Erreur de connexion. Veuillez réessayer.');
      setMessageType('error');
    }

    setIsSubmitting(false);
    
    // Clear message after 5 seconds
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  return (
    <footer className="bg-gradient-to-r from-gray-900 to-gray-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        
        {/* Newsletter Section - Top section */}
        <div className="text-center mb-16">
          <h4 className="text-2xl font-bold mb-6 flex items-center justify-center">
            <Mail className="h-6 w-6 mr-3" />
            Restez informé
          </h4>
          <p className="text-gray-300 text-lg mb-8 leading-relaxed max-w-2xl mx-auto">
            Recevez nos derniers guides et actualités tech directement dans votre boîte mail.
          </p>
          
          <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto space-y-4">
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Votre adresse email"
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 text-lg"
              />
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:transform-none flex items-center justify-center text-lg"
            >
              {isSubmitting ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
              ) : (
                <Send className="h-5 w-5 mr-3" />
              )}
              {isSubmitting ? 'Inscription...' : "S'inscrire"}
            </button>
          </form>
          
          {/* Message feedback */}
          {message && (
            <div className={`mt-4 p-4 rounded-lg flex items-center justify-center text-base max-w-md mx-auto ${
              messageType === 'success' 
                ? 'bg-green-800 bg-opacity-50 text-green-200' 
                : 'bg-red-800 bg-opacity-50 text-red-200'
            }`}>
              {messageType === 'success' ? (
                <CheckCircle className="h-5 w-5 mr-3 flex-shrink-0" />
              ) : (
                <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />
              )}
              {message}
            </div>
          )}
        </div>

        {/* 3 Columns Section */}
        <div className="border-t border-gray-700 pt-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            {/* Outils de Production */}
            <div>
              <h4 className="text-lg font-semibold mb-6 flex items-center">
                <Building2 className="h-5 w-5 mr-2" />
                Outils de Production
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/office365" className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center">
                    <Building2 className="h-4 w-4 mr-2 opacity-70" />
                    Office 365
                  </Link>
                </li>
                <li>
                  <Link to="/google-workspace" className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center">
                    <Chrome className="h-4 w-4 mr-2 opacity-70" />
                    Google Workspace
                  </Link>
                </li>
                <li>
                  <Link to="/synology" className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center">
                    <HardDrive className="h-4 w-4 mr-2 opacity-70" />
                    Synology NAS
                  </Link>
                </li>
              </ul>
            </div>

            {/* Technologie et IA */}
            <div>
              <h4 className="text-lg font-semibold mb-6 flex items-center">
                <Bot className="h-5 w-5 mr-2" />
                Technologie & IA
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/ia/conversational" className="text-gray-300 hover:text-white transition-colors duration-200">
                    IA Conversationnelles
                  </Link>
                </li>
                <li>
                  <Link to="/ia/creative" className="text-gray-300 hover:text-white transition-colors duration-200">
                    IA Créatives
                  </Link>
                </li>
                <li>
                  <Link to="/ia/specialized" className="text-gray-300 hover:text-white transition-colors duration-200">
                    IA Spécialisées
                  </Link>
                </li>
                <li>
                  <Link to="/mobile/apple" className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center">
                    <Smartphone className="h-4 w-4 mr-2 opacity-70" />
                    Écosystème Apple
                  </Link>
                </li>
                <li>
                  <Link to="/mobile/android" className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center">
                    <Bot className="h-4 w-4 mr-2 opacity-70" />
                    Écosystème Android
                  </Link>
                </li>
                <li>
                  <Link to="/operating-systems" className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center">
                    <Monitor className="h-4 w-4 mr-2 opacity-70" />
                    Systèmes OS
                  </Link>
                </li>
              </ul>
            </div>

            {/* Sécurité */}
            <div>
              <h4 className="text-lg font-semibold mb-6 flex items-center">
                <Shield className="h-5 w-5 mr-2" />
                Sécurité
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/security" className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center">
                    <Shield className="h-4 w-4 mr-2 opacity-70" />
                    Guides Sécurité
                  </Link>
                </li>
                <li>
                  <Link to="/security/guides/password-security" className="text-gray-300 hover:text-white transition-colors duration-200">
                    Mots de passe sécurisés
                  </Link>
                </li>
                <li>
                  <Link to="/security/guides/2fa-authentication" className="text-gray-300 hover:text-white transition-colors duration-200">
                    Authentification 2FA
                  </Link>
                </li>
                <li>
                  <a 
                    href="/sitemap.xml" 
                    className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="h-4 w-4 mr-2 opacity-70" />
                    Plan du site
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-700 mt-12 pt-8">
          <div className="text-center">
            <p className="text-gray-400 text-sm">
              © 2025 TICLIC.NET - Plateforme de formation digitale. Tous droits réservés.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;